import { addTimeoutToPromise } from '@src/app/utils/async';
import {
    GeoLocationCallback,
    GeoVault,
    GeoLocationPingResult,
    GeoVaultGatewayStatus,
    GeoPermissionState,
    GeoLocationPingStatus,
    GeoLocationErrorCode,
} from './types';
import {
    emitGeoLocationEvent,
    getGeoLocationActualPermission,
    getGeoLocationErrorCode,
    getTimePassedSinceLastGeoLocationPing,
    writeLastGeoLocationPingTime,
} from './helpers';
import { featureLogger } from '@src/app/logger/featureLogger';
import { getShellConfig } from '@src/config/shell/config';
import { clientStorageWithExpiry } from '@core/clientStorage/clientStorageWithExpiry';
import { record } from '@superbet-group/web.observability.sdk-hooks';
import {
    getBrowserName,
    getOperatingSystemName,
    Labels,
} from '@superbet-group/web.observability.sdk-core';
import { currentSite } from '@src/app/utils/domainHelpers';
import { LocationIdentifier } from '@core/services/location/locationObservability';
import { pingRetryController } from '@core/services/location/PingRetryController';

const ms = require('ms');

const lastGeoVaultLocalStorageKey = 'geoVault';
const timeoutClientId = '-1';
let isPeriodicallyPinging = false;

export async function pingLocation(identifier: LocationIdentifier): Promise<GeoLocationPingResult> {
    setIdentifier(identifier);

    let pingResult: GeoLocationPingResult;

    const permissionState = await getGeoLocationActualPermission();

    if (permissionState === GeoPermissionState.DENIED) {
        pingResult = {
            status: GeoLocationPingStatus.Blocked,
            code: GeoLocationErrorCode.PermissionError,
        };
    } else if (permissionState === GeoPermissionState.PROMPT) {
        pingResult = {
            status: GeoLocationPingStatus.PermissionPrompt,
        };
    } else {
        // initGeoLocationScript();
        // await initGeoLocationPromise;

        pingResult = await pingWithTimeoutAndRetry(identifier);
        if (pingResult.status !== GeoLocationPingStatus.Ok && pingResult.clientId === timeoutClientId) {
            // TODO: Review if this section still relevant after adding attribute `timedout` in metrics
            // ObservabilitySDK
            record(
                'geofencing_verification_timeout_total',
                1,
                {
                    browser_name: getBrowserName(),
                    os_name: getOperatingSystemName(),
                    site: currentSite,
                    country: getShellConfig().country,
                    reason: 'timeout',
                    failure_code: 'timeout',
                },
            );
            // AnalyticsSDK
            emitGeoLocationEvent('geofacing_verification_timeout_count', '1');
        }
    }

    if (pingResult.status === GeoLocationPingStatus.Unavailable) {
        featureLogger.error(new Error('GeoLocation ping timed out'));
    }

    notifyGeoLocationListeners(pingResult);

    writeLastGeoLocationPingTime();
    if (isPeriodicallyPinging) {
        scheduleGeoLocationPing(identifier);
    }
    return pingResult;
}

const ResultListenerActionMap: Record<GeoLocationPingStatus, keyof GeoLocationCallback> = {
    Ok: 'onAllow',
    Blocked: 'onBlock',
    Unavailable: 'onUnavailable',
    PermissionPrompt: 'onPermissionPrompt',
};

const gatewayErrorStatusResultMap: Record<Exclude<GeoVaultGatewayStatus, typeof GeoVaultGatewayStatus.Ok>, Exclude<GeoLocationPingStatus, typeof GeoLocationPingStatus.Ok>> = {
    [GeoVaultGatewayStatus.Blacklisted]: GeoLocationPingStatus.Blocked,
    [GeoVaultGatewayStatus.Blocked]: GeoLocationPingStatus.Blocked,
    [GeoVaultGatewayStatus.Error]: GeoLocationPingStatus.Unavailable,
    [GeoVaultGatewayStatus.IoError]: GeoLocationPingStatus.Unavailable,
    [GeoVaultGatewayStatus.IoTimeout]: GeoLocationPingStatus.Unavailable,
    [GeoVaultGatewayStatus.Limited]: GeoLocationPingStatus.Unavailable,
    [GeoVaultGatewayStatus.Outage]: GeoLocationPingStatus.Unavailable,
};

async function pingWrapper(identifier: LocationIdentifier) {
    const pingStartTime = Date.now();
    const geoVault = await window.mGeoJS!.ping();
    const pingEndTime = Date.now();

    pingInstrumentation(geoVault, (pingEndTime - pingStartTime), identifier);

    return geoVault;
}

/**
 * Sets identifier into GeoLocs. People can then search
 * pings by this identifier in GeoLocs admin search > identifier type > player identifier
 * @param identifier playerId if exists or hashed username (see locationObservability.ts)
 */
function setIdentifier(identifier: LocationIdentifier): void {
    window.mGeoJS!.setIdentitiesInVault({
        playerIdentifier: identifier.value,
    });
}

function pingInstrumentation(geoVault: GeoVault, durationInMs: number, identifier: LocationIdentifier) {
    const hasTimedout = getShellConfig().remoteConfig.geoLocationPingTimeoutMs < durationInMs;
    const willBeRetried = pingRetryController.mustRetry();

    // Verification Result
    if (geoVault.gatewayStatus !== GeoVaultGatewayStatus.Ok) {
        const labels = {
            success: 'false',
            country: getShellConfig().country,
            browser_name: getBrowserName(),
            os_name: getOperatingSystemName(),
            site: currentSite,
            reason: geoVault.gatewayStatus,
            failure_code: geoVault.failureCode ?? 'unknown',
            timedout: `${hasTimedout}`,
            will_be_retried: `${willBeRetried}`,
        };
        // ObservabilitySDK
        pingLatencyInstrumentation(durationInMs, labels);
        record('geofencing_verification_total', 1, labels);
        emitGeoLocationEvent(
            hasTimedout ? 'geofacing_timed_out_verification_error_count' : 'geofacing_verification_error_count',
            '1',
            geoVault.gatewayStatus,
            geoVault.failureCode,
            geoVault.personId,
            identifier.value,
        );
    } else {
        // ObservabilitySDK
        const labels = {
            success: 'true',
            browser_name: getBrowserName(),
            os_name: getOperatingSystemName(),
            site: currentSite,
            timedout: `${hasTimedout}`,
        };
        pingLatencyInstrumentation(durationInMs, labels);
        record('geofencing_verification_total', 1, labels);
        emitGeoLocationEvent('geofacing_verification_success_count', '1', `${hasTimedout}`);
    }
}
function pingLatencyInstrumentation(durationInMs: number, durationLabels: Labels) {
    // Latency
    // ObservabilitySDK
    record(
        'geofencing_verification_duration_seconds',
        durationInMs / 1000, // Convert from milli to seconds
        durationLabels,
    );
    // AnalyticsSDK
    emitGeoLocationEvent('geofacing_verification_duration_miliseconds', (durationInMs / 1000).toString());
}

async function pingWithTimeoutAndRetry(identifier: LocationIdentifier): Promise<GeoLocationPingResult> {
    const currentGeoVault = await addTimeoutToPromise<GeoVault | typeof timeoutClientId>(
        pingWrapper(identifier),
        getShellConfig().remoteConfig.geoLocationPingTimeoutMs,
        timeoutClientId,
    );
    if (currentGeoVault === timeoutClientId) {
        return {
            status: GeoLocationPingStatus.Unavailable,
            code: GeoLocationErrorCode.Timeout,
            clientId: timeoutClientId,
        };
    }

    const pingResult = handleGeoLocationPing(currentGeoVault);
    pingRetryController.registerPing(pingResult.status);
    if (pingRetryController.mustRetry()) {
        return pingWithTimeoutAndRetry(identifier);
    } else {
        updateGeoVault(currentGeoVault);
        return pingResult;
    }
}

function handleGeoLocationPing(geoVault: GeoVault): GeoLocationPingResult {
    // temporary to enable usage of dev tools
    if (geoVault.failureCode === '10.1' && !getShellConfig().remoteConfig.isProdGeoLocation) {
        return {
            status: GeoLocationPingStatus.Ok,
            geoLocationState: 'develop',
        };
    }
    const code = getGeoLocationErrorCode(geoVault);

    if (geoVault.gatewayStatus === GeoVaultGatewayStatus.Ok) {
        return {
            status: GeoLocationPingStatus.Ok,
            // for whitelisted IP address we receive OK withiut areaCode
            // so we default to our office location
            geoLocationState: geoVault.areaCode ?? 'brSaoPaulo',
        };
    } else {
        return {
            status: gatewayErrorStatusResultMap[geoVault.gatewayStatus],
            code,
            clientId: geoVault.personId,
        };
    }
}

function updateGeoVault(geoVault: GeoVault) {
    if (geoVault.gatewayStatus === GeoVaultGatewayStatus.Ok) {
        // two hours expiry, but lib function accepts days
        clientStorageWithExpiry.set(lastGeoVaultLocalStorageKey, geoVault, 2 / 24);
    }
}

export function getLastSavedGeoLocationState(): string | undefined | null {
    return clientStorageWithExpiry.get<GeoVault>('geoLocationState')?.areaCode;
}

let resolveGeoLocationScript: (value: boolean) => void;
let initGeoLocationPromise: Promise<any> | null = null;

export function initGeoLocationScript() {
    if (!initGeoLocationPromise) {
        initGeoLocationPromise = new Promise((resolve) => {
            resolveGeoLocationScript = resolve;
        });
        const geoLocsScript = document.createElement('script');
        geoLocsScript.src = `/location/${getShellConfig().remoteConfig.isProdGeoLocation ? 'prod' : 'non-prod'}/mGeoJS-4.0.9.min.js`;
        geoLocsScript.id = 'mgeo-engine';
        geoLocsScript.onload = async () => {
            await window.mGeoJS!.start();
            resolveGeoLocationScript(true);
        };
        document.head.appendChild(geoLocsScript);
    }
}

const geoLocationListeners = new Map<symbol, GeoLocationCallback>();

function notifyGeoLocationListeners(pingResult: GeoLocationPingResult) {
    const action = ResultListenerActionMap[pingResult.status];
    for (const key of geoLocationListeners.keys()) {
        geoLocationListeners.get(key)![action](pingResult);
    }
}

export function addGeoLocationListener(callback: GeoLocationCallback): symbol {
    const id = Symbol('geoLocationListener');
    geoLocationListeners.set(id, callback);
    return id;
}

export function removeGeoLocationListener(id: symbol) {
    geoLocationListeners.delete(id);
}

let pingScheduleId: number | null = null;
export function scheduleGeoLocationPing(identifier: LocationIdentifier) {
    const pingInterval = ms(`${getShellConfig().remoteConfig.geoLocationCheckPeriodMinutes}m`);
    const timePassedSinceLastPing = getTimePassedSinceLastGeoLocationPing();
    if (timePassedSinceLastPing === null || timePassedSinceLastPing >= pingInterval) {
        pingLocation(identifier);
    } else {
        if (pingScheduleId) {
            clearTimeout(pingScheduleId);
        }
        /**
         * 1. User lands on site and logs in at 1200, we ping
         * 2. Leaves the site
         * 3. Comes back in at 1220 -> we want to ping at 1230 and not at 1250
         * */
        pingScheduleId = setTimeout(pingLocation, pingInterval - timePassedSinceLastPing);
    }
}

export function startPeriodicallyPinging(identifier: LocationIdentifier) {
    isPeriodicallyPinging = true;
    scheduleGeoLocationPing(identifier);
}

export function stopPeriodicallyPinging() {
    isPeriodicallyPinging = false;
    if (pingScheduleId) {
        clearTimeout(pingScheduleId);
    }
}

// for testing purposes
export function resolveInitGeoLocationPromise() {
    initGeoLocationPromise = Promise.resolve();
}
